/**
 * GoalDetailModal — read-only public view of a single Mini-Project
 * Mission goal (DEV-1075 Phase 7.3a-ii).
 *
 * Opens when ANY viewer (Lead or otherwise) clicks the body of a
 * MiniProjectGoalCard. Shows the rich context the card itself can't
 * fit: TipTap description, photo strip, deadline, checklist items
 * (if applicable). Companion to the Lead-only GoalEditTray
 * (configuration) and GoalDataEntryTray (data entry).
 *
 * Centered modal pattern (matches MissionModal / StatusReader /
 * ScheduleModal) — not a side tray, because this is consumption, not
 * configuration. The user is paused on this surface to read, not to
 * fiddle with neighboring goals.
 *
 * TipTap content rendering: the stored JSON gets rendered through
 * @tiptap/react's `generateHTML` server-equivalent helper, which
 * walks the ProseMirror tree with the same extensions the editor
 * used to produce it. Without that we'd be rendering raw JSON
 * (useless) or trusting an unsanitized HTML projection (risky).
 *
 * Strings localized via `pages:miniProject.cards.goal.detail.*`.
 */

import React, { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { generateHTML } from "@tiptap/html";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import type {
  MiniProjectGoalView,
  DerivedChecklistItem,
} from "../../Data/MiniProjectConfig/miniProjectGoalView";
import StatusPhotoStrip, { type StatusPhotoItem } from "../StatusPhotoStrip/StatusPhotoStrip";
import styles from "./GoalDetailModal.module.css";
import { sanitizeRichText } from "../../utils/sanitizeHtml";

/* ── Types ─────────────────────────────────────────────────────── */

interface Props {
  open: boolean;
  onClose: () => void;
  /** The goal being viewed. May be null when the modal is animating
   *  closed; render-guard inside. */
  goal: MiniProjectGoalView | null;
}

/* ── Helpers ───────────────────────────────────────────────────── */

/** Render the TipTap JSON description into safe HTML using the same
 *  extension set GoalDescriptionEditor uses. Empty/null/malformed
 *  input returns null so the parent can short-circuit the section. */
function descriptionToHtml(richJson: string | null | undefined): string | null {
  if (!richJson || richJson.trim() === "") return null;
  try {
    const parsed = JSON.parse(richJson);
    return generateHTML(parsed, [
      StarterKit.configure({ heading: false }),
      Link.configure({ openOnClick: false }),
    ]);
  } catch {
    return null;
  }
}

function parseStoredPhotos(raw: string | null | undefined): StatusPhotoItem[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((p) => p && typeof p === "object" && typeof p.storageKey === "string")
      .map((p, idx) => ({
        storageKey: String(p.storageKey),
        caption: p.caption ?? null,
        position: typeof p.position === "number" ? p.position : idx,
      }));
  } catch {
    return [];
  }
}

function formatVal(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return "—";
  return Number.isInteger(v) ? String(v) : v.toFixed(2);
}

function formatCurrency(v: number | null | undefined, code: string): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return "—";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: code || "USD",
      maximumFractionDigits: 0,
    }).format(v);
  } catch {
    return `${v} ${code}`;
  }
}

function formatDeadline(iso: string | null | undefined): string | null {
  if (!iso) return null;
  try {
    const [y, m, d] = iso.split("-").map((s) => parseInt(s, 10));
    return new Date(y, (m || 1) - 1, d || 1).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

/* ── Component ─────────────────────────────────────────────────── */

const GoalDetailModal: React.FC<Props> = ({ open, onClose, goal }) => {
  const { t } = useAppTranslation("pages");

  // Esc to close.
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  // Memoize the rendered description HTML so it doesn't re-parse on
  // every render. Cheap re-parse but pointless when the goal doesn't
  // change.
  const descriptionHtml = useMemo(
    () => descriptionToHtml(goal?.descriptionRich),
    [goal?.descriptionRich],
  );

  const photos = useMemo(
    () => parseStoredPhotos(goal?.descriptionPhotos),
    [goal?.descriptionPhotos],
  );

  if (!open || !goal) return null;

  const isChecklist = goal.primitive === "checklist";
  const checklistItems: DerivedChecklistItem[] = goal.derivedChecklist ?? [];
  const checklistDone = checklistItems.filter((it) => it.checked).length;

  // Progress for the bar.
  let progressPct = 0;
  let isComplete = false;
  if (isChecklist) {
    const total = checklistItems.length;
    if (total > 0) {
      progressPct = (checklistDone / total) * 100;
      isComplete = checklistDone >= total;
    }
  } else if (goal.primitive === "percentage") {
    progressPct = Math.max(0, Math.min(100, goal.currentValue ?? 0));
    isComplete = progressPct >= 100;
  } else if (goal.targetValue && goal.targetValue > 0) {
    progressPct = Math.min(100, ((goal.currentValue ?? 0) / goal.targetValue) * 100);
    isComplete = (goal.currentValue ?? 0) >= goal.targetValue;
  }

  const dotState = isComplete ? "success" : "default";
  const deadlineDisplay = formatDeadline(goal.deadlineDate);

  // Value summary string per primitive.
  let valueSummary: React.ReactNode;
  if (isChecklist) {
    valueSummary = (
      <span className={styles.valueMain}>
        {t("miniProject.cards.goal.checklistComplete", {
          done: checklistDone,
          total: checklistItems.length,
        })}
      </span>
    );
  } else if (goal.primitive === "currency") {
    valueSummary = (
      <>
        <span className={styles.valueMain}>
          {formatCurrency(goal.currentValue ?? 0, goal.currencyCode || "USD")}
        </span>
        {goal.targetValue != null && (
          <span className={styles.valueTarget}>
            {t("miniProject.cards.goal.ofCurrency", { target: formatCurrency(goal.targetValue, goal.currencyCode || "USD") })}
          </span>
        )}
      </>
    );
  } else if (goal.primitive === "percentage") {
    valueSummary = <span className={styles.valueMain}>{formatVal(goal.currentValue ?? 0)}%</span>;
  } else {
    valueSummary = (
      <>
        <span className={styles.valueMain}>{formatVal(goal.currentValue ?? 0)}</span>
        <span className={styles.valueTarget}>
          {t("miniProject.cards.goal.of", { target: formatVal(goal.targetValue), unit: goal.unitKey ?? "" })}
        </span>
      </>
    );
  }

  const modal = (
    <div
      className={styles.backdrop}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-label={t("miniProject.cards.goal.detail.aria", { name: goal.nameKey })}
      >
        <header className={styles.header}>
          <div className={styles.headerLeft}>
            <span className={styles.statusDot} data-state={dotState} aria-hidden="true" />
            <h2 className={styles.title}>{goal.nameKey}</h2>
            {goal.required && (
              <span className={`${styles.badge} ${styles.badgeRequired}`}>
                {t("miniProject.cards.goal.requiredBadge")}
              </span>
            )}
          </div>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label={t("miniProject.cards.goal.detail.close")}
          >
            ×
          </button>
        </header>

        <div className={styles.body}>
          {/* Value summary + progress bar */}
          <div className={styles.valueRow}>{valueSummary}</div>
          <div className={styles.progressOuter}>
            <div
              className={styles.progressInner}
              data-state={dotState}
              style={{ inlineSize: `${progressPct}%` }}
            />
          </div>

          {/* Deadline */}
          {deadlineDisplay && (
            <div className={styles.deadlinePill}>
              <span className={styles.deadlineLabel}>
                {t("miniProject.sections.formLabel.deadline")}
              </span>
              <span className={styles.deadlineValue}>{deadlineDisplay}</span>
            </div>
          )}

          {/* Description */}
          {descriptionHtml && (
            <section className={styles.section}>
              <h3 className={styles.sectionHeading}>
                {t("miniProject.sections.formLabel.description")}
              </h3>
              <div
                className={styles.descriptionBody}
                dangerouslySetInnerHTML={{ __html: sanitizeRichText(descriptionHtml) }}
              />
            </section>
          )}

          {/* Photos */}
          {photos.length > 0 && (
            <section className={styles.section}>
              <h3 className={styles.sectionHeading}>
                {t("miniProject.sections.formLabel.photos")}
              </h3>
              <StatusPhotoStrip mode="read" photos={photos} />
            </section>
          )}

          {/* Checklist items (read-only display) */}
          {isChecklist && checklistItems.length > 0 && (
            <section className={styles.section}>
              <h3 className={styles.sectionHeading}>
                {t("miniProject.cards.goal.detail.checklistHeading")}
              </h3>
              <ul className={styles.checklist}>
                {checklistItems.map((it) => (
                  <li
                    key={it.itemId}
                    className={it.checked ? styles.checklistItemDone : styles.checklistItem}
                  >
                    <span className={styles.checklistMark} data-checked={it.checked} aria-hidden="true" />
                    <span>{it.label}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Empty state — no description, no photos, nothing extra to
              show beyond the value row + progress. Helps the modal not
              feel pointlessly bare. */}
          {!descriptionHtml && photos.length === 0 && !(isChecklist && checklistItems.length > 0) && !deadlineDisplay && (
            <div className={styles.empty}>
              {t("miniProject.cards.goal.detail.emptyDescription")}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default GoalDetailModal;
